import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { SETTINGS_CAPABILITIES } from '../../config/settingsCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import {
  useAppColors,
  usePreferences,
} from '../../context/PreferencesContext';
import { getOrganizationLocalization } from '../../services/settingsService';
import { isCompactWidth } from '../../utils/responsiveLayout';
import type {
  OrganizationLocalization,
  ThemePreference,
} from '../../types/settings';

type Props = NativeStackScreenProps<RootStackParamList, 'Settings'>;

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export function SettingsScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const preferences = usePreferences();
  const { width } = useWindowDimensions();
  const compact = isCompactWidth(width);
  const [localization, setLocalization] = useState<OrganizationLocalization | null>(
    null,
  );
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [message, setMessage] = useState('');
  const canViewOrg = auth.hasPermission(PERMISSIONS.SETTINGS_VIEW);

  const loadOrg = useCallback(async () => {
    if (!SETTINGS_CAPABILITIES.organizationLocalizationReadOnly || !canViewOrg) {
      setLocalization(null);
      return;
    }
    setLoadingOrg(true);
    try {
      setLocalization(await getOrganizationLocalization());
    } catch {
      setLocalization(null);
    } finally {
      setLoadingOrg(false);
    }
  }, [canViewOrg]);

  useEffect(() => {
    loadOrg().catch(() => undefined);
  }, [loadOrg]);

  async function selectTheme(theme: ThemePreference) {
    setSavingTheme(true);
    setMessage('');
    try {
      await preferences.setThemePreference(theme);
      setMessage('Theme preference saved on this device.');
    } catch {
      setMessage('Unable to save theme preference.');
    } finally {
      setSavingTheme(false);
    }
  }

  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.md,
      },
      body: { ...typography.body, color: palette.textSecondary },
      caption: { ...typography.caption, color: palette.textMuted },
      option: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '600' as const,
      },
      selected: {
        borderColor: palette.primary,
        backgroundColor: `${palette.primary}22`,
      },
      message: { ...typography.caption, color: palette.success, marginBottom: spacing.md },
    }),
    [palette],
  );

  return (
    <AppScreen webLayout="narrow" scroll>
      <ScreenHeader
        title="Settings"
        onBack={navigation.goBack}
      />

      <View style={styles.body}>
        {SETTINGS_CAPABILITIES.themeLocal ? (
          <AppCard style={styles.card}>
            <Text style={themed.section}>Appearance</Text>
            <Text style={themed.caption}>
              Theme is stored on this device. The backend has no user theme API.
            </Text>
            <View style={styles.options}>
              {THEME_OPTIONS.map(option => {
                const selected = preferences.themePreference === option.value;
                return (
                  <Pressable
                    key={option.value}
                    accessibilityRole="button"
                    accessibilityState={{ selected, disabled: savingTheme }}
                    accessibilityLabel={`${option.label} theme`}
                    disabled={savingTheme}
                    onPress={() => selectTheme(option.value)}
                    style={[
                      styles.option,
                      compact ? styles.optionCompact : null,
                      { borderColor: palette.border, backgroundColor: palette.background },
                      selected && themed.selected,
                    ]}
                  >
                    <Text style={themed.option}>{option.label}</Text>
                  </Pressable>
                );
              })}
            </View>
          </AppCard>
        ) : null}

        {SETTINGS_CAPABILITIES.notificationPreferences ? (
          <AppCard style={styles.card}>
            <Text style={themed.section}>Notifications</Text>
            <Text style={themed.body}>
              Push, email, and quiet hours are managed through the notification
              preferences API.
            </Text>
            <AppButton
              title="Notification preferences"
              onPress={() => navigation.navigate(routes.notificationPreferences)}
            />
          </AppCard>
        ) : null}

        {SETTINGS_CAPABILITIES.organizationLocalizationReadOnly && canViewOrg ? (
          <AppCard style={styles.card}>
            <Text style={themed.section}>Organization defaults</Text>
            <Text style={themed.caption}>
              Read from organization settings. There is no per-user timezone or
              format API.
            </Text>
            {loadingOrg ? <AppLoader message="Loading organization defaults" /> : null}
            {!loadingOrg && localization ? (
              <>
                <Text style={themed.body}>
                  Timezone: {localization.timeZone || 'Not available'}
                </Text>
                <Text style={themed.body}>
                  Date format: {localization.defaultDateFormat || 'Not available'}
                </Text>
                <Text style={themed.body}>
                  Time format: {localization.defaultTimeFormat || 'Not available'}
                </Text>
              </>
            ) : null}
            {!loadingOrg && !localization ? (
              <Text style={themed.caption}>Organization defaults are not available.</Text>
            ) : null}
          </AppCard>
        ) : null}

        {SETTINGS_CAPABILITIES.lateArrivalConfiguration && canViewOrg ? (
          <AppCard style={styles.card}>
            <Text style={themed.section}>Late Arrival Configuration</Text>
            <Text style={themed.caption}>
              Enable late arrival rules only when this organization should apply
              them. Face attendance does not require this configuration when it
              is turned off.
            </Text>
            <AppButton
              title="Late arrival settings"
              onPress={() => navigation.navigate(routes.lateArrivalConfiguration)}
            />
          </AppCard>
        ) : null}

        {message ? (
          <Text accessibilityRole="alert" style={themed.message}>
            {message}
          </Text>
        ) : null}

        <AppButton title="About" onPress={() => navigation.navigate(routes.about)} />
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  card: { gap: spacing.md, marginBottom: spacing.lg },
  options: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  option: {
    minHeight: 44,
    minWidth: 88,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionCompact: { flexGrow: 1, flexBasis: 0 },
});
